import type { GetCaseQueueResult } from '@whinerp/client';
import { formatCount } from '@whinerp/contracts/format';
import type { FormatContext } from '@whinerp/contracts/format';
import type { useT } from '../../app/i18n.js';

type Sla = GetCaseQueueResult['all'][number]['sla'];

/**
 * The SLA chip's words and tone, in one place.
 *
 * The queue column and the record header both show it, and two places
 * computing the sentence separately is two chances to disagree about whether a
 * case is breached - which is the one thing a support team reads off the
 * screen and acts on immediately.
 *
 * Colour AND word, always. A row that says "overdue" only by being red says
 * nothing in a printed handover sheet and nothing to an agent who cannot see
 * red.
 */
export interface SlaChip {
  /** The sentence. Never a bare number. */
  label: string;
  tone: 'danger' | 'warning' | 'success' | 'neutral';
  /** For assertions and for styling hooks. */
  urgency: Sla['urgency'];
}

/** Minutes as a duration somebody reads: "2h 10m", "45m", "3d". */
function duration(minutes: number, format: FormatContext, t: ReturnType<typeof useT>): string {
  const total = Math.abs(minutes);
  if (total >= 60 * 24) {
    const days = Math.floor(total / (60 * 24));
    return t('time.days', { values: { count: formatCount(days, format) } });
  }
  if (total >= 60) {
    const hours = Math.floor(total / 60);
    return t('time.hours', { values: { count: formatCount(hours, format) } });
  }
  return t('time.minutes', { values: { count: formatCount(total, format) } });
}

export function slaChip(sla: Sla, format: FormatContext, t: ReturnType<typeof useT>): SlaChip {
  const minutes =
    sla.clock === 'first-response' ? sla.firstResponseMinutes : sla.resolutionMinutes;
  const which =
    sla.clock === 'first-response' ? t('case.sla.firstResponse') : t('case.sla.resolution');

  if (sla.urgency === 'met') {
    return { label: t('case.sla.met'), tone: 'success', urgency: sla.urgency };
  }
  if (sla.urgency === 'none' || minutes === null) {
    // Not "on track": a case with no policy is not being measured, and saying
    // it is on track would be a figure with nothing behind it.
    return { label: t('case.sla.none'), tone: 'neutral', urgency: sla.urgency };
  }

  const spelled = duration(minutes, format, t);
  if (sla.urgency === 'breached') {
    return {
      label: `${t('case.sla.breached', { values: { duration: spelled } })} ${which}`,
      tone: 'danger',
      urgency: sla.urgency,
    };
  }
  if (sla.urgency === 'due-soon') {
    return {
      label: `${t('case.sla.dueSoon', { values: { duration: spelled } })} ${which}`,
      tone: 'warning',
      urgency: sla.urgency,
    };
  }
  return {
    label: `${t('case.sla.onTrack', { values: { duration: spelled } })} ${which}`,
    tone: 'neutral',
    urgency: sla.urgency,
  };
}
